<template>
  <v-app>
    <v-container>
      <h1>Notificaciones</h1>
      <v-form ref="form" v-model="formIsValid">
        <!-- Formulario de Configuración -->
        <v-card class="mb-4">
          <v-card-title>Configuración de Notificación</v-card-title>
          <v-card-text>
            <v-row>
              <!-- Seleccionar Máquina -->
              <v-col cols="12" sm="4">
                <v-select
                  v-model="maquinaSeleccionada"
                  :items="maquinas"
                  item-value="id_maquina"
                  item-text="maquina"
                  label="Seleccionar Máquina"
                  dense
                  outlined
                  @change="cargarVariables"
                  :rules="[v => !!v || 'Campo requerido']"
                ></v-select>
              </v-col>
              <!-- Seleccionar Variable -->
              <v-col cols="12" sm="8">
                <!-- {{ configurations }} -->
                <v-select
                  v-model="selectedVariable"
                  :items="variablesFiltradasComputadas"
                  label="Seleccionar Variable"
                  :item-text="variable_nombre" 
                  item-value="id_variable"
                  dense
                  outlined
                  @change="onVariableChange"
                  :disabled="!maquinaSeleccionada"
                  :rules="[v => !!v || 'Campo requerido']"
                ></v-select>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Inputs de Configuración -->
        <v-card class="mb-4">
          <v-card-title>Parámetros de la Anomalía</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="buffer_size"
                  label="Nº de datos de Recogida"
                  type="number"
                  dense
                  outlined
                  :rules="[v => !!v || 'Campo requerido', v => v >= 2 || 'Debe ser un número mayor o igual a 2']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="variability"
                  label="Variabilidad Entre Datos"
                  type="number"
                  dense
                  outlined
                  :rules="[v => !!v || 'Campo requerido', v => v >= 0 || 'Debe ser un número positivo']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="anomalousPercentage"
                  label="% de Valores Anómalos"
                  type="number"
                  dense
                  outlined
                  :rules="[v => !!v || 'Campo requerido', v => v > 0 || 'Debe ser un número mayor que 0', v => v <=100 || 'Debe ser un número menor o igual que 100']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="maxValue"
                  label="Valor Máximo"
                  type="number"
                  dense
                  outlined
                  :rules="[v => !!v || 'Campo requerido', v => v >= 0 || 'Debe ser un número positivo']"
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <v-text-field
                  v-model="minValue"
                  label="Valor Mínimo"
                  type="number"
                  dense
                  outlined
                  :rules="[v => !!v || 'Campo requerido', v => v >= 0 || 'Debe ser un número positivo']"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Botón para Guardar Configuración -->
        <v-btn color="primary" @click="saveConfig" :disabled="!formIsValid">
          Guardar Configuración
        </v-btn>
      </v-form>

      <!-- Tabla de Configuraciones Guardadas -->
      <v-data-table
        :headers="tableHeaders"
        :items="configurations"
        item-value="id"
        dense
        class="mt-4"
      >
      <template v-slot:[`item.variable`]="{ item }">
         {{ item.variable }} [{{ item.memoria }}]
        </template>
        <template v-slot:top>
          <v-toolbar flat>
            <v-toolbar-title>Configuraciones Guardadas</v-toolbar-title>
          </v-toolbar>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon @click="editConfig(item)">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon @click="deleteConfig(item.id)">
            <v-icon color="red">mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>

      <!-- Diálogo para Editar Configuración -->
      <v-dialog v-model="editDialog" max-width="600px">
        <v-card>
          <v-card-title>
            <span class="headline">Editar Configuración</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="editedItem.buffer_size"
                    label="Nº de datos de Recogida"
                    type="number"
                    dense
                    outlined
                    :rules="[v => !!v || 'Campo requerido', v => v >= 2 || 'Debe ser un número mayor o igual a 2']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="editedItem.variability"
                    label="Variabilidad Entre Datos"
                    type="number"
                    dense
                    outlined
                    :rules="[v => !!v || 'Campo requerido', v => v >= 0 || 'Debe ser un número positivo']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="editedItem.anomalousPercentage"
                    label="% de Valores Anomalos"
                    type="number"
                    dense
                    outlined
                    :rules="[v => !!v || 'Campo requerido', v => v > 0 || 'Debe ser un número mayor que 0', v => v <=100 || 'Debe ser un número menor o igual que 100']"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="editedItem.maxValue"
                    label="Valor Máximo"
                    type="number"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-text-field
                    v-model="editedItem.minValue"
                    label="Valor Mínimo"
                    type="number"
                    dense
                    outlined
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-btn color="blue darken-1" text @click="closeEdit">Cancelar</v-btn>
            <v-btn :disabled ="!formIsValidEdit" color="blue darken-1" text @click="saveEdit">Guardar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="text-h6">Confirmar eliminación</v-card-title>
          <v-card-text>
            ¿Estás seguro de que deseas eliminar esta notificación?
          </v-card-text>
          <v-card-actions>
            <v-btn color="red" text @click="confirmDelete">Eliminar</v-btn>
            <v-btn text @click="closeDeleteDialog">Cancelar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Sección de Notificaciones por Máquina -->
      <v-card class="mt-4">
        <v-card-title>Notificación en caso de pérdida de comunicación con servidor</v-card-title>
        <v-card-text>
          <v-row>
            <!-- Recorremos la lista de máquinas directamente -->
            <v-col
              cols="12"
              md="6"
              v-for="maquina in estadosMaquinas"
              :key="maquina.id"
            >
              <v-switch
                v-model="maquina.connection_loss_notification"
                :label="maquina.name"
                @change="actualizarEstado(maquina.id, maquina.connection_loss_notification)"
              ></v-switch>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </v-app>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  data() {
    return {
      
      maquinas: [],
      // maquinas: ["Desmoldeo","Macheria","Moldeo","Imf_Nave_2","Hornos1y2","Horno 1", "Horno 2"],
      deleteDialog: false,
      idToDelete: null,
      maquinaSeleccionada: null,
      selectedVariable: null,
      variables: [],// Aquí almacenamos todas las variables desde la API
      // variablesFiltradas: [], // Variables filtradas por la máquina seleccionada
      variableNombre: null, // Almacena el nombre de la variable
      variableMemoria: null, // Almacena la memoria asociada
      buffer_size: null,
      variability: null,
      anomalousPercentage: null,
      maxValue: null,
      minValue: null,
      formIsValid: false,
      configurations: [],
      tableHeaders: [
        { text: "Máquina", value: "maquina" },
        { text: "Variable", value: "variable" },
        { text: "Nº de datos de Recogida", value: "buffer_size" },
        { text: "Variabilidad", value: "variability" },
        { text: "% Anómalos", value: "anomalousPercentage" },
        { text: "Valor Máximo", value: "maxValue" },
        { text: "Valor Mínimo", value: "minValue" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      editedItem: {
        buffer_size: null,
        variability: null,
        anomalousPercentage: null,
        maxValue: null,
        minValue: null,
      },
      editDialog: false,
      editedItem: {},
      estadosMaquinas: [],
      variablesFiltradas: []
    };
  },

  computed: {
    variablesFiltradasComputadas() {
      const ids_variables_configuradas = new Set(this.configurations.map(v => v.id_variable));
      console.log(ids_variables_configuradas);
      
      return this.variablesFiltradas.filter(item => !ids_variables_configuradas.has(item.id_variable));
    },
    formIsValidEdit() {
      return (
        this.editedItem.buffer_size >= 2 &&
        this.editedItem.variability >= 0 &&
        this.editedItem.anomalousPercentage > 0 &&
        this.editedItem.anomalousPercentage <= 100
      );
    },

  },


  methods: {
    ...mapActions([
      "getNotificaciones",
      "postNotificaciones",
      "updateNotificacion",
      "deleteNotificacion",
      "doPublish",
      "getVariablesMaquina",
      "getConnectionLossNotification",
      "updateConnectionLossNotification",
      "getMaquinasYVariables"
    ]),

    variable_nombre(item){
        return item ? `${item.variable} - ${item.memoria}` : "";      
      },
    
    async cargarEstadosDesdeBackend() {
      try {
        // Llama al backend para obtener la lista de máquinas y sus estados
        const respuesta = await this.getConnectionLossNotification();
        console.log("Respuesta del backend:", respuesta);

         // Asignamos la lista de máquinas al estado
        this.estadosMaquinas = respuesta.map((maquina) => ({
          id: maquina.id,
          name: maquina.name,
          connection_loss_notification: maquina.connection_loss_notification,
        }));
      } catch (error) {
        console.error("Error al cargar estados desde el backend:", error);
      }
    },
    // Actualizar estado al cambiar el switch
    async actualizarEstado(id, nuevoEstado) {
      try {
        console.log(`Actualizando estado de la máquina con id ${id} a: ${nuevoEstado}`);

        // Llama al backend para actualizar el estado
        await this.updateConnectionLossNotification({
          id: id, // ID de la máquina
          connection_loss_notification: nuevoEstado,
        });
        this.publish();

        // Feedback en caso de éxito
        console.log(`Estado de la máquina con id ${id} actualizado correctamente.`);
      } catch (error) {
        console.error("Error al actualizar el estado:", error);
      }
    },
    
    onVariableChange(variableNombre) {
      const variable = this.variablesFiltradas.find(
        (v) => v.variable_nombre === variableNombre
      );
      this.variableNombre = variable?.variable_nombre || null;
      this.variableMemoria = variable?.memoria || null;
    },
    cargarVariables()
    {
      this.variablesFiltradas = this.maquinas.filter(m=> m.id_maquina==this.maquinaSeleccionada)
        .map(r=>r.variables)[0]
        // .filter(v=> v.es_alarma == false)
      console.log(this.variablesFiltradas)
    },


    resetForm() {
      this.maquinaSeleccionada = null;
      this.selectedVariable = null;
      this.buffer_size = null;
      this.variability = null;
      this.anomalousPercentage = null;
      this.maxValue = null;
      this.minValue = null;

      if (this.$refs.form) {
        this.$refs.form.reset();
      }
    },

    editConfig(item) {
      this.editedItem = Object.assign({}, item);
      this.editDialog = true;
    },

    async saveEdit() {
      try {
        /*
         'buffer_size' : this.buffer_size,
          'variability' : this.variability,
          'anomalous_percentage' : this.anomalousPercentage,
          'max_value' : this.maxValue,
          'min_value' : this.minValue,
        */
        const payload = {
          id: this.editedItem.id,
          buffer_size: this.editedItem.buffer_size,
          variability: this.editedItem.variability,
          anomalousPercentage: this.editedItem.anomalousPercentage,
          maxValue: this.editedItem.maxValue,
          minValue: this.editedItem.minValue,
        };

        const response = await this.updateNotificacion(payload);
        
        if (response.status === 200) {
          const index = this.configurations.findIndex((config) => config.id === this.editedItem.id);
          if (index !== -1) {
            this.configurations[index] = { ...this.editedItem };
          }
          this.editDialog = false;
          this.configuracionesGuardadas();
          this.publish();
        } else {
          throw new Error(response.data?.msg || "Error desconocido");
        }
      } catch (error) {
        console.error("Error al actualizar la notificación:", error);
        this.$toast.error("No se pudo actualizar la notificación.");
      }
    },

    closeEdit() {
      this.editDialog = false;
    },

    async configuracionesGuardadas() {
      try {
        const response = await this.getNotificaciones();
        console.log("notificaciones", response)
        if (response && response.data && Array.isArray(response.data)) {
          this.configurations = response.data
            .map((config) => ({
              id: config.id,
              memoria: config.memoria,
              id_variable: config.id_variable,
              maquina: config.nombre_maquina,
              variable: config.nombre_variable,
              buffer_size: config.buffer_size,
              variability: config.variability,
              anomalousPercentage: config.anomalous_percentage,
              maxValue: config.max_value,
              minValue: config.min_value,
            }))
            .sort((b, a) => a.id - b.id);
        } else {
          console.warn("No se encontraron datos de notificaciones.");
        }
      } catch (error) {
        console.error("Error al obtener las configuraciones guardadas:", error);
      }
    },

    async saveConfig() {
      if (this.$refs.form.validate()) {
        const newConfig = {
          'id_variable': this.selectedVariable,
          'buffer_size' : this.buffer_size,
          'variability' : this.variability,
          'anomalous_percentage' : this.anomalousPercentage,
          'max_value' : this.maxValue,
          'min_value' : this.minValue,
        };
      
      console.log(newConfig);
        try {
          const response = await this.postNotificaciones(newConfig);

          this.configurations.push({
            id: response.data.id,
            ...newConfig,
          });
          this.resetForm();
          this.configuracionesGuardadas();
          this.publish();
        } catch (error) {
          console.error("Error al guardar la configuración en la base de datos:", error);
        }
      }
    },

    deleteConfig(id) {
      this.idToDelete = id;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      try {
        if (this.idToDelete !== null) {
          await this.deleteNotificacion(this.idToDelete);
          await this.configuracionesGuardadas();
          this.publish();
        }
      } catch (error) {
        console.error("Error al eliminar la notificación:", error);
        this.$toast.error("No se pudo eliminar la notificación.");
      } finally {
        this.closeDeleteDialog();
      }
    },

    closeDeleteDialog() {
      this.deleteDialog = false;
      this.idToDelete = null;
    },

    publish() {
      let publication = {
        topic: "modificacionNotificaciones",
        qos: 2,
        message: JSON.stringify({ action: "updateNotification" }),
      };
      console.log("publish: " + publication.topic, publication.message);
      this.doPublish(publication);
    },

    async cargarMaquinasVariables() {
      const respuesta = await this.getMaquinasYVariables()
      console.log("maquinas", respuesta)
      this.maquinas = respuesta.data;
    },
  },
  mounted() {
    this.cargarMaquinasVariables();
    this.configuracionesGuardadas();
    this.cargarEstadosDesdeBackend();
  },
};
</script>
